import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Col, Row, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import ReactLoading from 'react-loading';
import { get, map, reduce, sum } from 'lodash-es';

import SalesTaxReportsTable from './SalesTaxReportsTable';
import { ALL_STATES_LABEL_OPTION, ALL_YEARS_LABEL_OPTION, MONTHS_LABELS } from './constants';
import Page from '../Page';
import { useAxios } from '../../hooks';
import { selectHouseRef } from '../../selectors';
import { formatJsonToCsv, formatToDecimalWithSeparator } from '../../utilities/textsFormatters';
import { getCurrentYear, getRangeOfPrevYears } from '../../utilities/dates';
import { salesTaxTableFormatters } from './salesTaxTableFormatters';

const blockEl = 'reports__sales-tax-reports';

const SalesTaxReports = () => {
  const currentYear = getCurrentYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(MONTHS_LABELS[0]);
  const [state, setState] = useState(ALL_STATES_LABEL_OPTION);
  const [isLoadingSalesTaxReport, setIsLoadingSalesTaxReport] = useState(null);
  const houseRef = useSelector(selectHouseRef);
  const { data: statesData } = useAxios('/app/states?country=US');

  // eslint-disable-next-line no-sequences
  const statesNameCodes = useMemo(() => reduce(get(statesData, 'data.data.states', []), (acc, { stateCode, stateName }) => (acc[stateName] = stateCode, acc), {
    [ALL_STATES_LABEL_OPTION]: ALL_STATES_LABEL_OPTION
  }), [statesData]);

  const { exec: taxReportsExec, data: taxReportsData, isError, isSuccess } = useAxios(
    () => {
      const url = `/app/house/${houseRef}/salesTaxReport`;

      if (year === ALL_YEARS_LABEL_OPTION
        && month === MONTHS_LABELS[0]
        && state === ALL_STATES_LABEL_OPTION
      ) {
        return url;
      }

      const searchParams = new URLSearchParams();

      if (year !== ALL_YEARS_LABEL_OPTION) {
        searchParams.set('year', year);
      }

      if (month !== MONTHS_LABELS[0]) {
        const monthIndex = MONTHS_LABELS.findIndex(monthLabel => monthLabel === month);

        searchParams.set('month', monthIndex);
      }

      if (state !== ALL_STATES_LABEL_OPTION) {
        searchParams.set('state', statesNameCodes[state]);
      }

      return `${url}?${searchParams.toString()}`;
    },
    [houseRef],
    null,
    { manual: true }
  );
  const isFetchingUserInfo = typeof houseRef === 'undefined';

  useEffect(() => {
    if (isSuccess) {
      setIsLoadingSalesTaxReport(false);
    } else if (isError) {
      setIsLoadingSalesTaxReport(null);
    }
  }, [isSuccess, isError]);

  const houseSalesTaxReportData = useMemo(() => get(taxReportsData, 'data.data.houseSalesTaxReport', []), [taxReportsData]);

  const totalGrossSales = useMemo(() => formatToDecimalWithSeparator(
    sum(map(houseSalesTaxReportData, 'salesAmount'))
  ), [houseSalesTaxReportData]);

  const totalSalesTaxCollected = useMemo(() => formatToDecimalWithSeparator(
    sum(map(houseSalesTaxReportData, 'taxAmount'))
  ), [houseSalesTaxReportData]);

  const yearsLabels = [ALL_YEARS_LABEL_OPTION].concat(getRangeOfPrevYears(2023, currentYear));

  const handleOnViewReport = () => {
    setIsLoadingSalesTaxReport(true);
    taxReportsExec();
  };

  const handleCsvDownload = () => {
    if (!houseSalesTaxReportData.length) {
      return;
    }

    const csvToDownload = formatJsonToCsv(
      houseSalesTaxReportData.map((rowData) => ({
        [salesTaxTableFormatters.reportDate.label]:
        salesTaxTableFormatters.reportDate.formatter(rowData),
        [salesTaxTableFormatters.state.label]:
        salesTaxTableFormatters.state.formatter(rowData),
        [salesTaxTableFormatters.grossSales.label]:
        `$${salesTaxTableFormatters.grossSales.formatter(rowData)}`,
        [salesTaxTableFormatters.salesTaxCollected.label]:
        `$${salesTaxTableFormatters.salesTaxCollected.formatter(rowData)}`,
      }))
    );
    const element = document.createElement('a');
    element.href = `data:text/csv;charset=utf-8,${encodeURI(csvToDownload)}`;
    element.target = '_blank';
    element.download = 'tax-report.csv';
    element.click();
  };

  const renderLoader = () => (
    <Col xs="12">
      <ReactLoading className="loading-spinner" type="spin" color="black" />
    </Col>
  );

  const renderSalesTaxReports = () => {
    if (isLoadingSalesTaxReport === null) {
      return null;
    }

    if (isLoadingSalesTaxReport) {
      return renderLoader();
    }

    if (houseSalesTaxReportData.length) {
      return (
        <>
          <Col className={`${blockEl}__totals-header d-flex`} xs="12">
            <div>
              <h3>Total Gross Sales</h3>
              <span>${totalGrossSales}</span>
            </div>
            <div>
              <h3>Total Sales Tax Collected</h3>
              <span>${totalSalesTaxCollected}</span>
            </div>
          </Col>
          <Col xs="12">
            <SalesTaxReportsTable data={houseSalesTaxReportData} />
          </Col>
        </>
      );
    }

    return (
      <Col className={`${blockEl}__totals-header d-flex`} xs="12">
        <div>
          <h3>There are no reports for the selected parameters.</h3>
          <span>
            Please update your selections and try again. If you need more assistance, please contact customer care for support.
          </span>
        </div>
      </Col>
    );
  };

  return (
    <Page className={blockEl} header={{ title: 'Sales Tax Reports', content: 'View your report prior to downloading or easily download a .CSV for your records.' }}>
      {isFetchingUserInfo
        ? (
          <Row>
            {renderLoader()}
          </Row>
        )
        : (
          <>
            <Row className={`${blockEl}__header-actions-container`}>
              <Col xs="12">
                <div className="d-inline-block">
                  <UncontrolledDropdown className="w-100">
                    <DropdownToggle caret color="white" className="text-truncate">
                      {year} &nbsp;
                    </DropdownToggle>
                    <DropdownMenu className="full-width-dropdown">
                      {yearsLabels.map(
                        (yearLabel) => (
                          <DropdownItem
                            key={yearLabel}
                            className="w-100 text-clamp-2 small-important"
                            onClick={() => setYear(yearLabel)}
                          >
                            {yearLabel}
                          </DropdownItem>
                        )
                      )}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
                <div className="d-inline-block">
                  <UncontrolledDropdown className="w-100">
                    <DropdownToggle caret color="white" className="text-truncate">
                      {month} &nbsp;
                    </DropdownToggle>
                    <DropdownMenu className="full-width-dropdown">
                      {MONTHS_LABELS.map(
                        (monthLabel) => (
                          <DropdownItem
                            key={monthLabel}
                            className="w-100 text-clamp-2 small-important"
                            onClick={() => setMonth(monthLabel)}
                          >
                            {monthLabel}
                          </DropdownItem>
                        )
                      )}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
                <div className="d-inline-block">
                  <UncontrolledDropdown className="w-100">
                    <DropdownToggle caret color="white" className="text-truncate">
                      {state} &nbsp;
                    </DropdownToggle>
                    <DropdownMenu className="full-width-dropdown">
                      {Object.keys(statesNameCodes).map(
                        (stateLabel) => (
                          <DropdownItem
                            key={stateLabel}
                            className="w-100 text-clamp-2 small-important"
                            onClick={() => setState(stateLabel)}
                          >
                            {stateLabel}
                          </DropdownItem>
                        )
                      )}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
                <Button
                  type="button"
                  color="secondary"
                  aria-label="View Report"
                  onClick={handleOnViewReport}
                >
                  View Report
                </Button>
                <Button
                  type="button"
                  color="primary"
                  aria-label="Download CSV"
                  disabled={!houseSalesTaxReportData.length}
                  onClick={handleCsvDownload}
                >
                  Download .CSV
                </Button>
              </Col>
            </Row>
            <Row className={`${blockEl}__content-container`}>
              {renderSalesTaxReports()}
            </Row>
          </>
        )}
    </Page>
  );
};

export default SalesTaxReports;
